/**
 * Account e ruoli (V49): chi c'e nella societa, con che ruolo nella
 * stagione corrente, e se puo ancora entrare.
 *
 * Disattivare esclude dalla societa alla richiesta successiva, senza
 * cancellare nulla: i ruoli restano e tornano validi alla riattivazione.
 * L'Admin Squadra non si disattiva da qui, e nessuno disattiva se stesso.
 *
 * Gli account nuovi si invitano (V50) e occupano un posto del limite da
 * subito. Finche l'invito non e accettato si reinvia o si annulla, e non si
 * disattiva: annullarlo cancella l'account, che non ha mai avuto accesso.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { InfoIcon, MailXIcon, SendIcon, UserCheckIcon, UserXIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MenuAzioni, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import {
  type AccountSocieta,
  ETICHETTA_RUOLO,
  type ElencoAccount,
  eAdmin,
  nomeSquadra,
  statoInvito,
} from './ruoli.js';
import { useConferma } from '../comuni/Conferma.js';

export function Account() {
  const { utente } = useContesto();
  const { dati, errore, ricarica } = useDati<ElencoAccount>('/amministrazione/account');
  const [inCorso, setInCorso] = useState<string | null>(null);
  const [chiedi, conferma] = useConferma();

  if (!dati)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  const agisci = async (a: AccountSocieta, lavoro: () => Promise<unknown>, esito?: string) => {
    setInCorso(a.id);
    try {
      await lavoro();
      await ricarica();
      if (esito) toast.success(esito);
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(null);
    }
  };

  const impostaStato = (a: AccountSocieta, attivo: boolean) =>
    agisci(
      a,
      () => chiama(`/amministrazione/account/${a.id}/stato`, { metodo: 'PUT', corpo: { attivo } }),
      `${a.nome} ${a.cognome}: account ${attivo ? 'riattivato' : 'disattivato'}.`,
    );
  const reinvia = (a: AccountSocieta) =>
    agisci(
      a,
      () => chiama(`/amministrazione/account/${a.id}/invito`, { metodo: 'POST' }),
      `Invito inviato di nuovo a ${a.email}.`,
    );
  const annulla = async (a: AccountSocieta) => {
    const si = await chiedi({
      titolo: `Annullare l'invito di ${a.nome} ${a.cognome}?`,
      testo: "L'account viene cancellato.",
      azione: "Annulla l'invito",
      distruttiva: true,
    });
    if (!si) return;
    void agisci(
      a,
      () => chiama(`/amministrazione/account/${a.id}/invito`, { metodo: 'DELETE' }),
      `Invito di ${a.email} annullato.`,
    );
  };

  const pieno = dati.limite.usato >= dati.limite.massimo;
  const invita = (
    <PulsanteNuovo
      etichetta="Invita account"
      a="/amministrazione/account/invita"
      disabled={pieno}
    />
  );

  return (
    <>
      <Testata titolo="Account e ruoli" voce="account" azione={invita} />
      <Corpo>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            <span className="tabular-nums">
              {dati.limite.usato} di {dati.limite.massimo} account,
            </span>{' '}
            contando inviti in attesa e account disattivati.
          </p>

          {pieno && (
            <Alert>
              <InfoIcon />
              <AlertTitle>Limite di account raggiunto</AlertTitle>
              <AlertDescription>
                La società ha già {dati.limite.massimo} account: non se ne possono invitare altri.
                Gli inviti non accettati si possono annullare; per alzare il limite contatta il
                gestore della piattaforma.
              </AlertDescription>
            </Alert>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead className="whitespace-normal">Ruoli nella stagione corrente</TableHead>
                <TableHead>
                  <span className="sr-only">Azioni</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dati.account.map((a) => {
                const correnti = a.ruoli.filter(
                  (r) => r.ruolo !== 'admin_squadra' && r.stagioneCorrente,
                );
                const passati = a.ruoli.filter(
                  (r) => r.ruolo !== 'admin_squadra' && !r.stagioneCorrente,
                ).length;
                const disattivabile = !eAdmin(a) && a.id !== utente.accountId;
                return (
                  <TableRow key={a.id}>
                    <TableCell>
                      <Link
                        to={`/amministrazione/account/${a.id}/ruoli`}
                        className="flex flex-col hover:underline"
                      >
                        <span className="flex items-center gap-2 font-medium">
                          {a.cognome} {a.nome}
                          {!a.attivo && <Badge variant="secondary">Disattivato</Badge>}
                          {a.invito && (
                            <Badge variant={a.invito.scaduto ? 'destructive' : 'outline'}>
                              {a.invito.scaduto ? 'Invito scaduto' : 'Invitato'}
                            </Badge>
                          )}
                        </span>
                        {a.invito && (
                          <span className="text-xs text-muted-foreground">
                            {statoInvito(a.invito)}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">{a.email}</span>
                      </Link>
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      <span className="flex flex-wrap items-center gap-1">
                        {eAdmin(a) && <Badge>Admin Squadra</Badge>}
                        {correnti.map((r) => (
                          <Badge key={r.id} variant="outline">
                            {ETICHETTA_RUOLO[r.ruolo]} · {nomeSquadra(r)}
                          </Badge>
                        ))}
                        {!eAdmin(a) && correnti.length === 0 && (
                          <span className="text-sm text-muted-foreground">Nessun ruolo</span>
                        )}
                        {passati > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {passati === 1
                              ? '+1 di stagioni passate'
                              : `+${passati} di stagioni passate`}
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <MenuAzioni
                          riga
                          etichetta={`Azioni su ${a.cognome} ${a.nome}`}
                          voci={[
                            a.invito && {
                              etichetta: 'Reinvia invito',
                              Icona: SendIcon,
                              disabled: inCorso === a.id,
                              onSelect: () => void reinvia(a),
                            },
                            a.invito && {
                              etichetta: 'Annulla invito',
                              Icona: MailXIcon,
                              disabled: inCorso === a.id,
                              onSelect: () => void annulla(a),
                              distruttiva: true,
                            },
                            disattivabile &&
                              !a.invito && {
                                etichetta: a.attivo ? 'Disattiva' : 'Riattiva',
                                Icona: a.attivo ? UserXIcon : UserCheckIcon,
                                disabled: inCorso === a.id,
                                onSelect: () => void impostaStato(a, !a.attivo),
                                distruttiva: a.attivo,
                              },
                          ]}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {conferma}
      </Corpo>
    </>
  );
}
